// Home › Performance in Blueprint (BL-123; pages/ux-full screens/dash.js, the design note
// home-dashboard-design): who sees which part, and the words and shapes the page draws from the
// answers of reports.businessFigures, reports.revenueTrend, reports.booking, reports.techScorecards and
// reports.techDetail. Each read is checked on the server; this only decides which to ask, so a part a
// person can't see is never asked for and nothing is refused.
import { DATE_PRESET_WORDS, DATE_PRESETS, type DatePreset } from '@flowology/domain';
import { money } from '@flowology/ui-web';
import type { BarPeriod } from '@flowology/ui-web/fl';
import { JOB_STATE_WORDS } from '@flowology/ui-web/words';

// ── who sees what (the design note's table), by the permissions the person holds (QA-681) ──

const all = (held: readonly string[], need: readonly string[]) => need.every((p) => held.includes(p));
/** The business (figures and the revenue chart): reports.view (the owner, the manager, the bookkeeper). */
export const seesBusiness = (held: readonly string[]) => all(held, ['reports.view']);
/** Every technician's scorecard: reports.scorecards.view with jobs.viewAll (the owner, the manager, the dispatcher). */
export const seesTechs = (held: readonly string[]) => all(held, ['reports.scorecards.view', 'jobs.viewAll']);
/** The phones: calls.view, for whoever books from them (jobs.book; a CSR the team's figures and their own row). */
export const seesPhones = (held: readonly string[]) => all(held, ['calls.view', 'jobs.book']);
/** A technician's own scorecard and no office part: Performance is their own scorecard, and their Home. */
export const ownScorecardOnly = (held: readonly string[]) =>
  held.includes('reports.scorecards.view') && !seesTechs(held) && !seesBusiness(held) && !seesPhones(held);
/** The permissions behind Home › Performance's parts: the business, the scorecards, the phones. */
export const PERFORMANCE_PERMISSIONS = ['reports.view', 'reports.scorecards.view', 'calls.view'];
/**
 * Who opens Home › Performance: whoever holds a permission behind a part of it, as their session says
 * (/auth/session), whichever of their roles gives it (BL-137), as Operations' pages ask since #173.
 */
export const mayPerformance = (held: readonly string[]): boolean =>
  PERFORMANCE_PERMISSIONS.some((p) => held.includes(p));

/** The page's title, by the question it answers for what they hold. */
export function performanceTitle(held: readonly string[]): string {
  if (seesBusiness(held)) return 'How the business is doing';
  if (seesTechs(held)) return 'How the team is doing';
  if (seesPhones(held)) return 'How booking is going';
  return 'Your scorecard';
}

// ── the dates ──

export const PRESETS: readonly { id: DatePreset; label: string }[] = DATE_PRESETS.map((id) => ({
  id,
  label: DATE_PRESET_WORDS[id],
}));

export interface RangeOut {
  preset: DatePreset;
  from: string;
  to: string;
  compareFrom: string;
  compareTo: string;
  weekly: boolean;
  words: string;
  compareWords: string;
}

/** What the dates are and what they're compared with: "1–21 October 2026, against 1–21 October 2025." */
export const rangeLine = (r: RangeOut): string =>
  `${r.words}, against ${r.compareWords}${r.weekly ? ', the same weekdays' : ''}.`;

/** The input every read takes: a preset, or a custom range's two days. */
export function datesInput(preset: DatePreset, custom: { from: string; to: string }) {
  return preset === 'custom' ? { dates: preset, from: custom.from, to: custom.to } : { dates: preset };
}

// ── words ──

/** A share (0–1) as a whole percent, "55 %"; a dash for none. */
export const pct = (x: number | null | undefined): string =>
  x === null || x === undefined ? '—' : `${Math.round(x * 100)} %`;

/** Whole dollars, "$113,800", for figures that are never about cents. */
export const dollars = (cents: number): string => money(Math.round(cents / 100) * 100).replace(/\.00$/, '');

/** "$113.8k", "$1.2M": the chart's ticks and its one direct label. */
export function shortMoney(cents: number): string {
  const v = cents / 100;
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(v / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(a >= 1e5 ? 0 : 1).replace(/\.0$/, '')}k`;
  return `$${Math.round(v)}`;
}

/** How a figure compares with a year before, in words. */
export function versus(cur: number, prev: number): string {
  if (!prev) return cur ? 'Nothing a year before' : 'None a year before either';
  const c = Math.round(((cur - prev) / prev) * 100);
  return c === 0 ? 'Same as a year before' : `${Math.abs(c)} % ${c > 0 ? 'more' : 'less'} than a year before`;
}

/** How a rate compares with a year before, in points. */
export function versusRate(cur: number | null, prev: number | null): string {
  if (cur === null || prev === null) return 'Nothing to compare a year before';
  const p = Math.round((cur - prev) * 100);
  return p === 0
    ? 'Same as a year before'
    : `${Math.abs(p)} point${Math.abs(p) === 1 ? '' : 's'} ${p > 0 ? 'higher' : 'lower'} than a year before`;
}

/** A rating out of 5, "4.8"; a dash with none. */
export const stars = (rating: number | null, places = 1): string =>
  rating === null ? '—' : rating.toFixed(places);

// ── the answers ──

export interface Figures {
  revenueCents: number;
  grossProfitCents?: number;
  jobsCompleted: number;
  opportunities: number;
  sold: number;
  soldCents: number;
  conversion: number | null;
  opportunityAverageCents: number | null;
  membershipOpportunities: number;
  membershipsSold: number;
  membershipConversion: number | null;
  leadsSet: number | null;
  leadSalesCents: number | null;
  ratings: number;
  rating: number | null;
}
export interface Business {
  range: RangeOut;
  current: Figures;
  yearBefore: Figures;
}
export interface MonthPair {
  month: string;
  label: string;
  year: string;
  revenueCents: number;
  yearBeforeCents: number;
  inRange: boolean;
  toDate: { yearBeforeCents: number; through: string } | null;
}
export interface Trend {
  range: RangeOut;
  months: MonthPair[];
  revenueCents: number;
  yearBeforeCents: number;
}
export interface BookingRow {
  key: string | null;
  label: string;
  calls: number;
  leads: number;
  booked: number;
  rate: number | null;
  you: boolean;
}
export interface Booking {
  range: RangeOut;
  team: { calls: number; leads: number; booked: number; rate: number | null };
  yearBeforeRate: number | null;
  target: number;
  rows: BookingRow[];
  hidden: number;
}
export interface Card {
  staffId: string;
  name: string;
  businessUnit: string | null;
  businessUnitId: string | null;
  awayUntil: string | null;
  isNew: boolean;
  current: Figures;
  yearBefore: Figures;
}
export interface Targets {
  conversion: number;
  memberships: number;
}
export interface Scorecards {
  range: RangeOut;
  targets: Targets;
  technicians: Card[];
}
export const JOB_STATUSES = [
  'paid',
  'owed',
  'no_charge',
  'to_review',
  'under_way',
  'booked',
  'cancelled',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export interface JobRow {
  jobId: string;
  number: string;
  customerName: string;
  date: string;
  jobTypeId: string;
  jobType: string;
  /** None for a job with no department (QA-720). */
  businessUnitId: string | null;
  businessUnit: string | null;
  status: JobStatus;
  amountCents: number;
  grossProfitCents?: number;
}
export interface JobList {
  rows: JobRow[];
  total: number;
  totalCents: number;
}
export interface Detail {
  range: RangeOut;
  targets: Targets;
  scorecard: Card;
  months: MonthPair[];
  completed: JobList;
  sold: JobList;
  choices: {
    businessUnits: { id: string; name: string }[];
    jobTypes: { id: string; name: string; businessUnitId: string | null }[];
    statuses: JobStatus[];
  };
}

// where a job stands says the app's one word for it (job-words); a done job here says its money
export const STATUS_WORDS: Record<JobStatus, string> = {
  paid: 'Paid',
  owed: 'Invoiced, owed',
  no_charge: 'No charge',
  to_review: JOB_STATE_WORDS.review,
  under_way: JOB_STATE_WORDS.underway,
  booked: JOB_STATE_WORDS.booked,
  cancelled: JOB_STATE_WORDS.cancelled,
};
/** Waiting on someone: a dashed underline (red stays for a problem that needs someone now). */
export const statusWaits = (s: JobStatus): boolean => s === 'to_review' || s === 'booked' || s === 'owed';

// ── the title block's figures (at most five) ──

export interface Fig {
  label: string;
  value: string;
  sub: string;
}

/** The business's five: revenue, gross profit (or memberships sold), jobs, conversion, opportunity average. */
export function businessFigs(b: Business): Fig[] {
  const c = b.current;
  const p = b.yearBefore;
  const gp = c.grossProfitCents;
  return [
    { label: 'Revenue', value: dollars(c.revenueCents), sub: versus(c.revenueCents, p.revenueCents) },
    gp === undefined
      ? {
          label: 'Memberships sold',
          value: String(c.membershipsSold),
          sub: versus(c.membershipsSold, p.membershipsSold),
        }
      : {
          label: 'Gross profit',
          value: dollars(gp),
          sub: `${pct(c.revenueCents ? gp / c.revenueCents : null)} of revenue. ${versus(gp, p.grossProfitCents ?? 0)}`,
        },
    {
      label: 'Jobs completed',
      value: c.jobsCompleted.toLocaleString('en-CA'),
      sub: versus(c.jobsCompleted, p.jobsCompleted),
    },
    {
      label: 'Conversion rate',
      value: pct(c.conversion),
      sub: c.conversion === null ? 'Too few opportunities to rate' : versusRate(c.conversion, p.conversion),
    },
    {
      label: 'Opportunity average',
      value: c.opportunityAverageCents === null ? '—' : dollars(c.opportunityAverageCents),
      sub: versus(c.opportunityAverageCents ?? 0, p.opportunityAverageCents ?? 0),
    },
  ];
}

/**
 * The dispatcher's figures: the technicians' and the phones', no money of the business's. A job two
 * technicians shared counts for each of them, as each one's scorecard counts it.
 */
export function teamFigs(cards: readonly Card[], booking: Booking | null): Fig[] {
  const sum = (f: (x: Figures) => number, which: 'current' | 'yearBefore') =>
    cards.reduce((s, c) => s + f(c[which]), 0);
  const opps = sum((x) => x.opportunities, 'current');
  const sold = sum((x) => x.sold, 'current');
  const prevOpps = sum((x) => x.opportunities, 'yearBefore');
  const prevSold = sum((x) => x.sold, 'yearBefore');
  const ratings = sum((x) => x.ratings, 'current');
  const starsTotal = cards.reduce((s, c) => s + (c.current.rating ?? 0) * c.current.ratings, 0);
  const conv = opps >= 5 ? sold / opps : null;
  const out: Fig[] = [
    {
      label: 'Opportunities',
      value: opps.toLocaleString('en-CA'),
      sub: `${sold} sold. ${versus(opps, prevOpps)}`,
    },
    {
      label: 'Conversion rate',
      value: pct(conv),
      sub:
        conv === null
          ? 'Too few opportunities to rate'
          : versusRate(conv, prevOpps >= 5 ? prevSold / prevOpps : null),
    },
    {
      label: 'Customer satisfaction',
      value: ratings ? stars(starsTotal / ratings, 2) : '—',
      sub: ratings ? `of 5, from ${ratings} rating${ratings === 1 ? '' : 's'}` : 'No ratings in these dates',
    },
  ];
  if (booking)
    out.push({
      label: 'Booking rate',
      value: pct(booking.team.rate),
      sub: versusRate(booking.team.rate, booking.yearBeforeRate),
    });
  return out;
}

/** A CSR's own figures: their calls, what could be booked, what they booked, their rate. */
export function ownPhoneFigs(b: Booking): Fig[] {
  const me = b.rows.find((r) => r.you) ?? { calls: 0, leads: 0, booked: 0, rate: null };
  return [
    { label: 'Your calls', value: me.calls.toLocaleString('en-CA'), sub: b.range.words },
    { label: 'Could be booked', value: me.leads.toLocaleString('en-CA'), sub: 'someone asking for new work' },
    {
      label: 'You booked',
      value: me.booked.toLocaleString('en-CA'),
      sub: `of ${me.leads.toLocaleString('en-CA')}`,
    },
    {
      label: 'Your booking rate',
      value: pct(me.rate),
      sub: `Target ${pct(b.target)}${me.rate !== null && me.rate < b.target ? ', under it' : ''}`,
    },
  ];
}

/** One technician's figures on their page. */
export function techFigs(card: Card, targets: Targets): Fig[] {
  const c = card.current;
  const p = card.yearBefore;
  return [
    { label: 'Revenue', value: dollars(c.revenueCents), sub: versus(c.revenueCents, p.revenueCents) },
    ...(c.grossProfitCents === undefined
      ? []
      : [
          {
            label: 'Gross profit',
            value: dollars(c.grossProfitCents),
            sub: `${pct(c.revenueCents ? c.grossProfitCents / c.revenueCents : null)} of their revenue`,
          },
        ]),
    {
      label: 'Conversion rate',
      value: pct(c.conversion),
      sub:
        c.conversion === null
          ? 'Too few opportunities to rate'
          : `${c.sold} of ${c.opportunities}. Target ${pct(targets.conversion)}`,
    },
    { label: 'Opportunities', value: String(c.opportunities), sub: versus(c.opportunities, p.opportunities) },
    ...(c.grossProfitCents === undefined
      ? [
          {
            label: 'Opportunity average',
            value: c.opportunityAverageCents === null ? '—' : dollars(c.opportunityAverageCents),
            sub: versus(c.opportunityAverageCents ?? 0, p.opportunityAverageCents ?? 0),
          },
        ]
      : []),
    {
      label: 'Customer satisfaction',
      value: stars(c.rating),
      sub: c.ratings ? `of 5, ${c.ratings} rating${c.ratings === 1 ? '' : 's'}` : 'No ratings in these dates',
    },
  ];
}

/** The sentence over the chart: "This month, 1–21 October 2026: $113,800, 12 % less than 1–21 October 2025." */
export function trendSummary(t: Trend): string {
  const c = t.yearBeforeCents
    ? Math.round(((t.revenueCents - t.yearBeforeCents) / t.yearBeforeCents) * 100)
    : null;
  const name = PRESETS.find((p) => p.id === t.range.preset)?.label ?? '';
  return `${name}, ${t.range.words}: ${dollars(t.revenueCents)}${
    c === null
      ? ''
      : c === 0
        ? `, the same as ${t.range.compareWords}`
        : `, ${Math.abs(c)} % ${c > 0 ? 'more' : 'less'} than ${t.range.compareWords}`
  }.`;
}

/** A month's values in words, for its hover, its focus and its row. */
export function monthWords(m: MonthPair): string {
  const prevYear = String(Number(m.year) - 1);
  return `${m.label} ${m.year}: ${dollars(m.revenueCents)}${m.toDate ? ' so far' : ''}. ${m.label} ${prevYear}: ${dollars(
    m.yearBeforeCents,
  )}${m.toDate ? `, ${dollars(m.toDate.yearBeforeCents)} by ${m.toDate.through}` : ''}.`;
}

// ── the chart (ui-web's BarChart) ──

/** Each month as BarChart's period: its revenue beside the same month a year before, the month under
 * way split at the same day, the dates' months on the band. */
export function monthPeriods(months: readonly MonthPair[]): BarPeriod[] {
  return months.map((m) => ({
    id: m.month,
    label: m.label,
    group: m.year,
    value: m.revenueCents,
    compare: m.yearBeforeCents,
    ...(m.toDate ? { compareSoFar: m.toDate.yearBeforeCents } : {}),
    inBand: m.inRange,
    words: monthWords(m),
    valueText: `${dollars(m.revenueCents)}${m.toDate ? ' so far' : ''}`,
    compareText: m.toDate
      ? `${dollars(m.toDate.yearBeforeCents)} by ${m.toDate.through}`
      : dollars(m.yearBeforeCents),
  }));
}

/** The latest month's figures at the chart's right: "$114k so far", "$129k by 27 Oct a year before". */
export function chartEnd(months: readonly MonthPair[]): { main: string; sub: string } | null {
  const last = months.at(-1);
  if (!last) return null;
  return {
    main: `${shortMoney(last.revenueCents)}${last.toDate ? ' so far' : ''}`,
    sub: last.toDate
      ? `${shortMoney(last.toDate.yearBeforeCents)} by ${last.toDate.through} a year before`
      : `${shortMoney(last.yearBeforeCents)} a year before`,
  };
}

/** A month's change against a year before (to the same day for the month under way), or null with none. */
export function monthChange(m: MonthPair): number | null {
  const base = m.toDate ? m.toDate.yearBeforeCents : m.yearBeforeCents;
  return base ? Math.round(((m.revenueCents - base) / base) * 100) : null;
}

// ── the technicians ──

export type SortBy = 'revenue' | 'conversion' | 'rating';
export const SORTS: readonly { id: SortBy; label: string }[] = [
  { id: 'revenue', label: 'Revenue' },
  { id: 'conversion', label: 'Conversion' },
  { id: 'rating', label: 'Rating' },
];

/** Blueprint has eight technician pens: the first eight have one, the rest a muted key. */
export const PENS = 8;
export const penFor = (index: number): number | null => (index < PENS ? index + 1 : null);

/** The cells in order: filtered by business unit, sorted (a rate too few to rate sorts last). */
export function cardsShown(cards: readonly Card[], sort: SortBy, unitId: string | null): Card[] {
  const key = (c: Card) =>
    sort === 'revenue'
      ? c.current.revenueCents
      : sort === 'conversion'
        ? (c.current.conversion ?? -1)
        : c.current.ratings
          ? (c.current.rating ?? -1)
          : -1;
  return cards
    .filter((c) => !unitId || c.businessUnitId === unitId)
    .sort((a, b) => key(b) - key(a) || a.name.localeCompare(b.name));
}

/** The business units the technicians were in, for the filter. */
export const unitsOf = (cards: readonly Card[]) =>
  [
    ...new Map(
      cards.flatMap((c) => (c.businessUnitId ? [[c.businessUnitId, c.businessUnit ?? ''] as const] : [])),
    ),
  ]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

/** "Away until 23 October", "Started this year", or nothing. */
export function cardState(c: Card): string | null {
  if (c.awayUntil) {
    const [, m, d] = c.awayUntil.split('-').map(Number);
    const month = new Date(Date.UTC(2000, (m ?? 1) - 1, 1)).toLocaleString('en-CA', {
      month: 'long',
      timeZone: 'UTC',
    });
    return `Away until ${d} ${month}`;
  }
  return c.isNew ? 'Started this year' : null;
}

/** A cell's revenue line: against a year before, or new this year. */
export const revenueLine = (c: Card): string =>
  c.isNew && !c.yearBefore.revenueCents
    ? 'Started this year'
    : versus(c.current.revenueCents, c.yearBefore.revenueCents);

/** Under the target says so in words; at or over, the target alone. */
export const targetWords = (v: number | null, target: number): { text: string; under: boolean } | null =>
  v === null
    ? null
    : v < target
      ? { text: `under the ${pct(target)} target`, under: true }
      : { text: `Target ${pct(target)}`, under: false };
